import { TelemetrySampler } from './telemetrySampler'
import { getAudioSentryPort, type AudioSentryPort } from './audioSentryPort'
import type { DeadAirEpisode, DeadAirSink } from './deadAirTypes'
import { isIPadOS } from './iPadOS'

/**
 * audioEpisodeSink — the one place a confirmed dead-air episode is written to
 * BOTH sinks.
 *
 * - **Sentry** holds the durable per-episode bundle (rich, per-peer): a
 *   `captureMessage('meeting_dead_air')` with queryable tags, the context ring
 *   as a stringified `audioRing` context, and the raw `stats` snapshot.
 *   Per-failure-type fingerprint keeps them from collapsing into one issue.
 *   Sentry is reached through an injected port (see audioSentryPort).
 * - **GA** holds the compact aggregate matrix: one `meeting_dead_air_scan` row
 *   of scalars only (GA silently truncates at 4000 rows/room and caps params,
 *   so it can't carry the per-episode bundle — that's why Sentry is the durable
 *   store).
 *
 * Both are anomaly-gated through the min-interval sampler (never 1-in-N): the
 * GA scan per `(listener)`, each Sentry peer message per `(listener, peer)`, so
 * a single stuck peer can't re-fire every cycle or drown out another peer.
 *
 * Browser, version and OS are not stamped here — GA4 and Sentry both derive
 * them from the User-Agent. Only `iPadOS` is, because an iPad reports a
 * desktop-Macintosh UA and both sinks would otherwise file it as a Mac.
 */

/**
 * The context ring, as this sink reads it: one call, at emit time.
 *
 * Typed as a one-method port rather than the concrete buffer so the ~30s
 * context-capture machinery is optional. A sink with no ring still writes the
 * full episode to both destinations; it just carries no lead-up.
 */
export interface EpisodeContextRing {
  serialize(): string
}

/**
 * GA string values are capped at 100 chars.
 *
 * These fields are comma-joined lists, so a blind slice ends the value on a
 * half-identity (`peerA,peerB,peer`) that matches nothing and groups as its own
 * bucket in aggregate queries. Cutting back to the last separator inside the
 * limit keeps every entry that survives whole. A single entry longer than the
 * cap has no separator to fall back to and is sliced as before.
 */
const truncate = (s: string, max = 100): string => {
  if (s.length <= max) return s
  const cut = s.slice(0, max)
  const lastComma = cut.lastIndexOf(',')
  return lastComma === -1 ? cut : cut.slice(0, lastComma)
}

/**
 * Run an app-supplied side effect, swallowing anything it throws. Returns the
 * value, or undefined if it threw — see {@link AudioEpisodeSink.emit}.
 */
function guarded<T>(fn: () => T): T | undefined {
  try {
    return fn()
  } catch {
    return undefined
  }
}

export interface AudioEpisodeSinkDeps {
  /** GA transport (repository.logEvent). */
  logEvent: (name: string, params: Record<string, unknown>) => void
  /** The min-interval anomaly sampler (shared with the detector). */
  sampler: TelemetrySampler
  /** The context ring; serialized into the Sentry bundle when one is wired. */
  ring?: EpisodeContextRing
  /** Lazy Sentry-port accessor (defaults to the registered global port). */
  sentry?: () => AudioSentryPort | null
  /** Injectable clock (defaults to Date.now). */
  now?: () => number
}

export interface ReloadFlushOptions {
  /** Passed to Sentry.flush — how long to wait for the transport to drain. */
  flushTimeoutMs?: number
  /**
   * Only flush when an episode was emitted within this window before the page
   * left; an unrelated reload long after the last episode needs no flush.
   */
  pendingWindowMs?: number
}

const DEFAULT_FLUSH_TIMEOUT_MS = 2000
const DEFAULT_PENDING_WINDOW_MS = 8000

export class AudioEpisodeSink implements DeadAirSink {
  private readonly deps: AudioEpisodeSinkDeps
  private readonly sentry: () => AudioSentryPort | null
  private readonly now: () => number
  /** When the last durable episode was emitted (for the reload flush). */
  private lastEmitAt: number | null = null
  private reloadFlushCleanup: (() => void) | null = null

  constructor(deps: AudioEpisodeSinkDeps) {
    this.deps = deps
    this.sentry = deps.sentry ?? getAudioSentryPort
    this.now = deps.now ?? Date.now
  }

  /**
   * Write a confirmed episode to both sinks. No-op when there are no dead
   * peers — GA only fires when peersDead > 0, and there is nothing durable to
   * capture otherwise.
   *
   * Every step is individually guarded. This runs inside a livekit
   * `trackSubscribed` listener and inside the slide transition, neither of
   * which catches: a throw from the GA transport or the Sentry port would abort
   * the remaining livekit listeners for that event or propagate out of the
   * slide change. Diagnostics must never break the thing they are measuring,
   * least of all at the moment they exist to observe.
   */
  emit(episode: DeadAirEpisode): void {
    const peersDead = episode.deadPeers.length
    if (peersDead === 0) return

    guarded(() => this.emitScanRow(episode, peersDead))
    guarded(() => this.emitSentryPeers(episode, peersDead))
    // Mark the moment for reload survival: the async Sentry transport may not
    // have drained if the page reloads in the next instant.
    this.lastEmitAt = this.now()
  }

  /**
   * Reload survival: the durable episode is a client-side Sentry event, and the
   * dominant coping behavior in the corpus is an immediate page reload — which
   * can drop the event before its async transport leaves. Register a
   * `pagehide` / `visibilitychange → hidden` handler that flushes Sentry when a
   * fresh episode is still in flight. Firebase Analytics already flushes its
   * own queue on unload, so only the Sentry side needs an explicit flush here.
   *
   * Returns a cleanup fn; also stored so {@link removeReloadFlush} can undo it.
   *
   * Installing twice is a no-op that hands back the standing cleanup. Only one
   * cleanup can be tracked, so a second install would strand the first pair of
   * listeners: `removeReloadFlush` would unwind the newer pair and leave the
   * older one attached for the life of the page. The listeners are idempotent
   * on the flush itself, so the cost is a leak rather than a double-flush, but
   * a re-mount or an HMR pass is enough to hit it.
   */
  installReloadFlush(options: ReloadFlushOptions = {}): () => void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return () => {}
    }
    if (this.reloadFlushCleanup) return this.reloadFlushCleanup
    const flushTimeout = options.flushTimeoutMs ?? DEFAULT_FLUSH_TIMEOUT_MS
    const windowMs = options.pendingWindowMs ?? DEFAULT_PENDING_WINDOW_MS

    const flushIfPending = () => {
      if (this.lastEmitAt === null) return
      if (this.now() - this.lastEmitAt > windowMs) return
      // Guarded on both axes, the same rule the rest of the file follows: a
      // port that throws synchronously must not break `pagehide`, and a flush
      // that rejects must not surface as an unhandled rejection during unload,
      // which some browsers report as a page error. Nothing reads the result —
      // there is no recovery from a failed flush at this point in the page's
      // life, only the choice not to make it someone else's error.
      guarded(() => {
        void this.sentry()
          ?.flush(flushTimeout)
          .catch(() => {})
      })
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushIfPending()
    }

    window.addEventListener('pagehide', flushIfPending)
    document.addEventListener('visibilitychange', onVisibility)
    const cleanup = () => {
      window.removeEventListener('pagehide', flushIfPending)
      document.removeEventListener('visibilitychange', onVisibility)
      this.reloadFlushCleanup = null
    }
    this.reloadFlushCleanup = cleanup
    return cleanup
  }

  removeReloadFlush(): void {
    this.reloadFlushCleanup?.()
  }

  /** GA aggregate matrix row — scalars only, one per scan. */
  private emitScanRow(episode: DeadAirEpisode, peersDead: number): void {
    const scanKey = TelemetrySampler.anomalyKey(
      'meeting_dead_air_scan',
      episode.listenerId
    )
    if (!this.deps.sampler.allowAnomaly(scanKey)) return

    const params: Record<string, unknown> = {
      peersDead,
      peersSubscribed: episode.peersSubscribed,
      peersPublishing: episode.peersPublishing,
      pageHidden: episode.pageHidden,
      secondsSinceSlideChange: episode.secondsSinceSlideChange ?? -1,
      triggeringSpeaker: episode.triggeringSpeaker ?? '',
      // Deliberately raw, in roster order, and deliberately not the same shape
      // as the Sentry `forks` tag, which is deduped and sorted. These two are
      // positionally paired: index i of `forks` is the failure type of index i
      // of `deadPeers`, which is the only way a GA row of scalars can say which
      // peer hit which fault. Deduping would destroy that pairing on every row.
      // The Sentry side is a queryable tag rather than a list, so there the
      // stable deduped form is the useful one. The pairing degrades if either
      // value overruns the 100-char cap and truncates to a different count —
      // acceptable, because peersDead is 1-3 in the cases these rows answer.
      deadPeers: truncate(episode.deadPeers.map((p) => p.identity).join(',')),
      forks: truncate(episode.deadPeers.map((p) => p.fork).join(',')),
      iPadOS: isIPadOS(),
    }
    // Pairwise convenience: single-peer scans carry the failure type directly.
    if (peersDead === 1) params.fork = episode.deadPeers[0].fork
    this.deps.logEvent('meeting_dead_air_scan', params)
  }

  /** Durable Sentry bundle — one captureMessage per dead peer. */
  private emitSentryPeers(episode: DeadAirEpisode, peersDead: number): void {
    const port = this.sentry()
    if (!port) return

    const audioRing = this.deps.ring?.serialize()
    // The whole episode's failure-type set, carried on every per-peer message.
    // The per-peer throttle can drop a sibling peer's message (same pair
    // re-firing, backoff), so a reader of any single delivered report would
    // otherwise see only `peersDead: N` and this peer's own failure — a count,
    // not the second diagnosis. This list makes one report enough to see the
    // client was hitting more than one failure at once. Distinct + sorted so
    // it's a stable tag.
    const forks = [...new Set(episode.deadPeers.map((p) => p.fork))]
      .sort()
      .join(',')
    const deadPeersDetail = episode.deadPeers.map((p) => ({
      identity: p.identity,
      fork: p.fork,
    }))
    for (const peer of episode.deadPeers) {
      const key = TelemetrySampler.anomalyKey(
        'meeting_dead_air',
        episode.listenerId,
        peer.identity
      )
      // Backoff: this key fans out per dead peer and stays hot for as long as
      // the client is deaf, so a flat interval bills the most events during a
      // fleet-wide outage. The first few describe the fault; the rest are
      // copies. Reporting never stops — a silent client would read as
      // recovered — it just slows to the ceiling.
      if (!this.deps.sampler.allowAnomaly(key, { backoff: true })) continue

      const tags: Record<string, string | number | boolean> = {
        iPadOS: isIPadOS(),
        // Who this report is about. The identity is otherwise only inside the
        // `deadPeers` roster context, which names every dead peer and so can't
        // say which one this message diagnoses — and contexts aren't queryable,
        // so "every report against this peer" needs a tag. Tagged and not
        // fingerprinted on purpose: grouping stays per failure type, or a
        // fleet-wide fault would open one issue per participant.
        peer: peer.identity,
        fork: peer.fork,
        // Every distinct failure type in this episode (this peer's included),
        // so the tag is queryable for "more than one failure at once" even from
        // a single delivered message.
        forks,
        peersDead,
        pageHidden: episode.pageHidden,
      }

      const contexts: Record<string, Record<string, unknown>> = {
        // Two contexts, not one blob: the wiring picture answers "was this
        // client ever set up to hear the peer", the receiver stats answer
        // "did the audio arrive and decode". A report that mixes them makes
        // the reader sort thirty keys to find out which question failed.
        wiring: { snapshot: peer.wiring ?? null },
        receiverStats: { snapshot: peer.receiverStats ?? null },
        // The full dead-peer roster (identity + failure type), so one report
        // names the other concurrent issues rather than just counting them.
        deadPeers: { list: deadPeersDetail },
      }
      // Stringified so Sentry's normalizeDepth/maxValueLength can't mangle it.
      if (audioRing !== undefined) contexts.audioRing = { entries: audioRing }

      // Guarded per peer, not once around the loop: a whole-client episode is
      // the case where the sibling reports matter most, and a single throwing
      // message would otherwise take every peer after it with it. Peers that
      // never reach `allowAnomaly` keep their sampler slot and can report on
      // the next scan, so the loss is confined to a transient episode — which
      // is exactly the kind that never comes back.
      guarded(() =>
        port.captureMessage({
          message: 'meeting_dead_air',
          level: 'warning',
          tags,
          contexts,
          // Per-failure-type fingerprint so pairwise/global ones stay separate
          // issues.
          fingerprint: ['meeting_dead_air', peer.fork],
        })
      )
    }
  }
}
